import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Brain,
  Check,
  Clock3,
  ExternalLink,
  Gift,
  RefreshCw,
  ShieldCheck,
  ShoppingBag,
  Sparkles,
  X
} from 'lucide-react';
import { useAuth } from '../auth';
import { api, ApiClientError } from '../api';
import type { MarketRewardCandidate, Redemption, Reward } from '../types';
import { Loading } from '../components/Loading';
import { useToast } from '../components/Toast';

interface EligibleResponse {
  pointBalance: number;
  remainingBudgetVnd: number;
  rewards: Reward[];
}

interface SuggestionsResponse {
  job: { id: string; status: string; createdAt: string; completedAt?: string; error?: string } | null;
  suggestions: Reward[];
}

function money(value: number): string {
  return value === 0 ? 'Không tốn tiền' : `${new Intl.NumberFormat('vi-VN').format(value)}đ`;
}

function moneyRange(min: number, max: number): string {
  return min === max ? money(max) : `${money(min)} – ${money(max)}`;
}

function points(value: number): string {
  return new Intl.NumberFormat('vi-VN').format(value);
}

export function RewardsPage() {
  const { selectedChild, selectedChildId } = useAuth();
  const { show } = useToast();
  const [eligible, setEligible] = useState<EligibleResponse | null>(null);
  const [suggestions, setSuggestions] = useState<SuggestionsResponse | null>(null);
  const [redemptions, setRedemptions] = useState<Redemption[]>([]);
  const [marketCandidates, setMarketCandidates] = useState<MarketRewardCandidate[]>([]);
  const [candidatePoints, setCandidatePoints] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');

  const load = useCallback(async (silent = false) => {
    if (!selectedChildId) return;
    if (!silent) setLoading(true);
    try {
      const [eligibleData, suggestionData, redemptionData, candidateData] = await Promise.all([
        api<EligibleResponse>(`/rewards/eligible?childId=${encodeURIComponent(selectedChildId)}`),
        api<SuggestionsResponse>(`/ai/suggestions?childId=${encodeURIComponent(selectedChildId)}`),
        api<Redemption[]>('/redemptions'),
        api<MarketRewardCandidate[]>('/market-candidates')
      ]);
      setEligible(eligibleData);
      setSuggestions(suggestionData);
      setRedemptions(redemptionData);
      setMarketCandidates(candidateData);
      setCandidatePoints((current) => {
        const next = { ...current };
        for (const candidate of candidateData) {
          if (!next[candidate.id]) next[candidate.id] = 300;
        }
        return next;
      });
    } catch (error) {
      if (!silent) show(error instanceof ApiClientError ? error.message : 'Không thể tải quà.', 'error');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [selectedChildId, show]);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(true), 20_000);
    return () => window.clearInterval(timer);
  }, [load]);

  const suggestionMap = useMemo(
    () => new Map((suggestions?.suggestions ?? []).map((item) => [item.id, item])),
    [suggestions]
  );

  const displayRewards = useMemo(() => {
    if (!eligible) return [];
    return [...eligible.rewards].sort((a, b) => {
      const aSuggestion = suggestionMap.get(a.id);
      const bSuggestion = suggestionMap.get(b.id);
      if (aSuggestion && bSuggestion) return (aSuggestion.rank ?? 99) - (bSuggestion.rank ?? 99);
      if (aSuggestion) return -1;
      if (bSuggestion) return 1;
      return a.pointCost - b.pointCost;
    });
  }, [eligible, suggestionMap]);

  const pendingCandidates = marketCandidates.filter((item) => item.status === 'pending');

  async function requestSuggestions() {
    if (!selectedChildId) return;
    setBusy('ai');
    try {
      const result = await api<{ eligibleCount: number }>('/ai/jobs', {
        method: 'POST',
        body: { childId: selectedChildId }
      });
      show(
        result.eligibleCount > 0
          ? 'Đã tạo yêu cầu. ChatGPT sẽ đọc và ghi kết quả trực tiếp qua Kidee MCP.'
          : 'Đã tạo yêu cầu tìm quà thị trường; hiện chưa có quà trong catalog đủ điều kiện để xếp hạng.',
        'success'
      );
      await load(true);
    } catch (error) {
      show(error instanceof ApiClientError ? error.message : 'Không thể tạo yêu cầu AI.', 'error');
    } finally {
      setBusy('');
    }
  }

  async function requestReward(reward: Reward) {
    if (!selectedChildId) return;
    setBusy(`reward:${reward.id}`);
    try {
      await api('/redemptions', {
        method: 'POST',
        body: { childId: selectedChildId, rewardId: reward.id, note: '' }
      });
      show(`Đã gửi yêu cầu đổi “${reward.title}” để ba mẹ duyệt.`, 'success');
      await load(true);
    } catch (error) {
      show(error instanceof ApiClientError ? error.message : 'Không thể yêu cầu đổi quà.', 'error');
    } finally {
      setBusy('');
    }
  }

  async function decide(redemptionId: string, action: 'approve' | 'reject' | 'fulfill') {
    setBusy(`redemption:${redemptionId}`);
    try {
      await api(`/redemptions/${encodeURIComponent(redemptionId)}/${action}`, { method: 'POST' });
      show(
        action === 'approve' ? 'Đã duyệt đổi quà và trừ điểm.' : action === 'reject' ? 'Đã từ chối yêu cầu.' : 'Đã đánh dấu đã trao quà.',
        'success'
      );
      await load(true);
    } catch (error) {
      show(error instanceof ApiClientError ? error.message : 'Không thể xử lý yêu cầu.', 'error');
    } finally {
      setBusy('');
    }
  }

  async function approveCandidate(candidate: MarketRewardCandidate) {
    const pointCost = Math.trunc(candidatePoints[candidate.id] ?? 0);
    if (pointCost < 1) {
      show('Hãy đặt số điểm đổi lớn hơn 0.', 'error');
      return;
    }
    setBusy(`candidate:${candidate.id}`);
    try {
      await api(`/market-candidates/${encodeURIComponent(candidate.id)}/approve`, {
        method: 'POST',
        body: { pointCost, cooldownDays: 30, icon: '🎁' }
      });
      show(`Đã thêm “${candidate.title}” vào catalog quà của ${selectedChild?.nickname}.`, 'success');
      await load(true);
    } catch (error) {
      show(error instanceof ApiClientError ? error.message : 'Không thể duyệt món quà này.', 'error');
    } finally {
      setBusy('');
    }
  }

  async function rejectCandidate(candidate: MarketRewardCandidate) {
    setBusy(`candidate:${candidate.id}`);
    try {
      await api(`/market-candidates/${encodeURIComponent(candidate.id)}/reject`, { method: 'POST' });
      show('Đã bỏ qua gợi ý này.', 'success');
      await load(true);
    } catch (error) {
      show(error instanceof ApiClientError ? error.message : 'Không thể bỏ qua gợi ý.', 'error');
    } finally {
      setBusy('');
    }
  }

  if (loading && !eligible) return <Loading label="Đang tìm những món quà phù hợp…" />;
  if (!eligible) return null;

  const pending = redemptions.filter((item) => item.status === 'requested');
  const approved = redemptions.filter((item) => item.status === 'approved');
  const jobWaiting = suggestions?.job?.status === 'pending' || suggestions?.job?.status === 'exported';

  return (
    <div className="page rewards-page">
      <div className="page-title page-title--center">
        <span className="eyebrow"><Gift size={15} /> Catalog do ba mẹ kiểm soát</span>
        <h1>Gợi ý quà phù hợp</h1>
        <p>{selectedChild?.nickname} có <strong>{points(eligible.pointBalance)} điểm</strong>.</p>
      </div>

      <section className="reward-balance-card">
        <div><span>⭐</span><strong>{points(eligible.pointBalance)}</strong><small>điểm hiện có</small></div>
        <div><span>💰</span><strong>{money(eligible.remainingBudgetVnd)}</strong><small>ngân sách còn lại</small></div>
      </section>

      <section className="guardrail-banner">
        <ShieldCheck size={24} />
        <div><strong>Ba mẹ vẫn là người quyết định cuối cùng</strong><p>ChatGPT đọc dữ liệu giả danh qua MCP; kết quả được Kidee nhập từ GitHub JSON. Quà mới vẫn cần ba mẹ đặt điểm và duyệt.</p></div>
      </section>

      <section className="ai-status-card">
        <div className="ai-status-card__icon"><Brain size={25} /></div>
        <div>
          <strong>{jobWaiting ? 'Đang chờ kết quả AI' : suggestions?.suggestions.length || pendingCandidates.length ? 'Gợi ý AI đã sẵn sàng' : 'Tạo gợi ý cá nhân hóa'}</strong>
          <p>
            {jobWaiting
              ? 'ChatGPT đọc job qua MCP, ghi JSON vào GitHub mailbox và Kidee sẽ tự nhập. Trang tự kiểm tra mỗi 20 giây.'
              : suggestions?.job?.error
                ? `Lần xử lý gần nhất có lỗi: ${suggestions.job.error}`
                : 'AI có thể xếp hạng catalog và tìm sản phẩm đang mua được tại Việt Nam.'}
          </p>
        </div>
        <button className="button button--soft" type="button" onClick={() => void requestSuggestions()} disabled={busy === 'ai' || jobWaiting}>
          {busy === 'ai' ? <span className="mini-spinner" /> : <Sparkles size={17} />}
          {jobWaiting ? 'Đang xử lý' : 'Gợi ý mới'}
        </button>
      </section>

      {pendingCandidates.length > 0 && (
        <section className="market-candidate-section">
          <div className="section-heading section-heading--stack">
            <div><span className="section-icon section-icon--green"><ShoppingBag size={19} /></span><h2>Quà ChatGPT tìm mua được tại Việt Nam</h2></div>
            <p>Kiểm tra link và giá, đặt số điểm rồi duyệt để biến thành quà có thể đổi.</p>
          </div>
          <div className="market-candidate-list">
            {pendingCandidates.map((candidate) => (
              <article key={candidate.id} className="market-candidate-card">
                <div className="market-candidate-card__top">
                  <span className="match-badge">★ {candidate.matchScore}% phù hợp</span>
                  <span>{candidate.store}{candidate.seller ? ` · ${candidate.seller}` : ''}</span>
                </div>
                <h3>{candidate.title}</h3>
                <p>{candidate.reason}</p>
                <div className="reward-meta">
                  <span>{moneyRange(candidate.priceMinVnd, candidate.priceMaxVnd)}</span>
                  <span>{candidate.category.replace('_', ' ')}</span>
                  <span>{candidate.availabilityNote}</span>
                </div>
                <a className="market-product-link" href={candidate.productUrl} target="_blank" rel="noreferrer"><ExternalLink size={16} /> Xem nơi bán</a>
                <div className="market-candidate-actions">
                  <label>Điểm đổi<input type="number" min="1" max="1000000" value={candidatePoints[candidate.id] ?? 300} onChange={(event) => setCandidatePoints({ ...candidatePoints, [candidate.id]: Number(event.target.value) })} /></label>
                  <button type="button" className="button button--primary button--small" disabled={Boolean(busy)} onClick={() => void approveCandidate(candidate)}><Check size={16} /> Thêm vào catalog</button>
                  <button type="button" className="button button--soft button--small" disabled={Boolean(busy)} onClick={() => void rejectCandidate(candidate)}><X size={16} /> Bỏ qua</button>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {displayRewards.length > 0 ? (
        <section className="reward-list">
          {displayRewards.map((reward, index) => {
            const suggestion = suggestionMap.get(reward.id);
            return (
              <article key={reward.id} className="reward-card">
                <div className="reward-card__visual">
                  {suggestion && <span className="rank-badge">{suggestion.rank ?? index + 1}</span>}
                  <span aria-hidden="true">{reward.icon}</span>
                  <b>{points(reward.pointCost)} điểm ⭐</b>
                </div>
                <div className="reward-card__body">
                  <div className="reward-card__title">
                    <h2>{reward.title}</h2>
                    {suggestion?.score !== undefined && <span className="match-badge">★ {suggestion.score}% phù hợp</span>}
                  </div>
                  <p>{suggestion?.reason ?? reward.description}</p>
                  <div className="reward-meta"><span>{money(reward.priceVnd)}</span><span>{reward.category.replace('_', ' ')}</span>{reward.cooldownDays > 0 && <span><Clock3 size={14} /> {reward.cooldownDays} ngày</span>}</div>
                  <button className="button button--primary" type="button" disabled={Boolean(busy)} onClick={() => void requestReward(reward)}>
                    {busy === `reward:${reward.id}` ? 'Đang gửi…' : 'Yêu cầu đổi quà'}
                  </button>
                </div>
              </article>
            );
          })}
        </section>
      ) : (
        <section className="empty-state surface"><span>🌱</span><h2>Chưa có quà trong catalog đủ điều kiện</h2><p>Con có thể tích thêm điểm, hoặc ba mẹ duyệt một gợi ý thị trường ở phía trên.</p></section>
      )}

      {(pending.length > 0 || approved.length > 0) && (
        <section className="surface approval-section">
          <div className="section-heading"><div><span className="section-icon section-icon--orange"><Gift size={19} /></span><h2>Ba mẹ xử lý đổi quà</h2></div></div>
          <div className="approval-list">
            {[...pending, ...approved].map((item) => (
              <div key={item.id} className="approval-row">
                <span className="approval-emoji">{item.rewardIcon}</span>
                <div><strong>{item.childNickname} · {item.rewardTitle}</strong><small>{points(item.pointsSpent)} điểm · {money(item.priceVnd)}</small></div>
                {item.status === 'requested' ? (
                  <div className="approval-actions">
                    <button type="button" className="icon-action icon-action--accept" aria-label="Duyệt" disabled={Boolean(busy)} onClick={() => void decide(item.id, 'approve')}><Check size={18} /></button>
                    <button type="button" className="icon-action icon-action--reject" aria-label="Từ chối" disabled={Boolean(busy)} onClick={() => void decide(item.id, 'reject')}><X size={18} /></button>
                  </div>
                ) : (
                  <button type="button" className="button button--small button--soft" disabled={Boolean(busy)} onClick={() => void decide(item.id, 'fulfill')}>Đã trao quà</button>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      <button type="button" className="refresh-button" onClick={() => void load()}><RefreshCw size={16} /> Làm mới</button>
    </div>
  );
}
